type QueryParams = Record<string, string | string[] | undefined>
type QueryParamsStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>

const affiliateQueryParamNames = [
  'promo',
  'partner',
  'utm_source',
  'utm_medium',
  'ehub',
] as const

export const getAffiliateQueryParams = (
  queryParams: QueryParams
): QueryParams =>
  affiliateQueryParamNames.reduce<QueryParams>((affiliateQueryParams, name) => {
    const value = queryParams[name]

    return value === undefined
      ? affiliateQueryParams
      : { ...affiliateQueryParams, [name]: value }
  }, {})

const getStorageKey = (sessionId: string) =>
  `twisto:flow:${sessionId}:affiliateQueryParams`

const hasQueryParams = (queryParams: QueryParams) =>
  Object.keys(queryParams).length > 0

const isQueryParams = (value: unknown): value is QueryParams =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const getSessionAffiliateQueryParams = (
  sessionId: string,
  queryParams: QueryParams,
  storage: QueryParamsStorage
): QueryParams => {
  const affiliateQueryParams = getAffiliateQueryParams(queryParams)
  const storageKey = getStorageKey(sessionId)

  if (hasQueryParams(affiliateQueryParams)) {
    storage.setItem(storageKey, JSON.stringify(affiliateQueryParams))

    return affiliateQueryParams
  }

  const storedQueryParams = storage.getItem(storageKey)

  if (!storedQueryParams) {
    return {}
  }

  try {
    const parsedQueryParams: unknown = JSON.parse(storedQueryParams)

    if (isQueryParams(parsedQueryParams)) {
      return getAffiliateQueryParams(parsedQueryParams)
    }
  } catch {
    storage.removeItem(storageKey)
  }

  return {}
}

export const getAffiliateQueryString = (queryParams: QueryParams): string => {
  const searchParams = new URLSearchParams()

  Object.entries(getAffiliateQueryParams(queryParams)).forEach(
    ([name, value]) => {
      const values = Array.isArray(value) ? value : [value]

      values.forEach((item) => {
        if (item !== undefined) {
          searchParams.append(name, item)
        }
      })
    }
  )

  const queryString = searchParams.toString()

  return queryString ? `?${queryString}` : ''
}
